import { App, URLOpenListenerEvent } from '@capacitor/app';
import { createContext, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

// Define supported deep link types
export type DeepLinkType = 'post' | 'profile' | 'game' | 'club' | 'chat';

// Interface for parsed deep link data
export interface DeepLinkData {
  type: DeepLinkType;
  id: string;
  params?: Record<string, string>;
}

/**
 * Parse a deep link URL into structured data
 * Format: mgp://{type}/{id}?{params}
 */
export const parseDeepLink = (url: string): DeepLinkData | null => {
  try {
    // Parse the URL
    const urlObj = new URL(url);
    
    // Check if it's our custom scheme
    if (urlObj.protocol !== 'mgp:') {
      return null;
    }
    
    // Remove leading slashes and split path
    const path = urlObj.pathname.replace(/^\/+/, '');
    const segments = path.split('/');
    
    // Need at least type and id
    if (segments.length < 2) {
      console.warn('Invalid deep link format:', url);
      return null;
    }
    
    // Extract type and ID
    const type = segments[0] as DeepLinkType;
    const id = segments[1];
    
    // Validate type
    if (!['post', 'profile', 'game', 'club', 'chat'].includes(type)) {
      console.warn('Unknown deep link type:', type);
      return null;
    }
    
    // Parse query parameters
    const params: Record<string, string> = {};
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    
    return { type, id, params };
  } catch (error) {
    console.error('Error parsing deep link:', error);
    return null;
  }
};

/**
 * Map deep link data to app routes
 */
export const getRouteFromDeepLink = (data: DeepLinkData): string => {
  switch (data.type) {
    case 'post':
      return `/feed/post/${data.id}`;
    case 'profile':
      return `/profile/${data.id}`;
    case 'game':
      return `/games/${data.id}`;
    case 'club':
      return `/clubs/${data.id}`;
    case 'chat':
      return `/messages/c/${data.id}`;
    default:
      return '/';
  }
};

/**
 * Hook to initialize deep link handling
 */
const useDeepLinks = () => {
  const history = useHistory();
  
  useEffect(() => {
    const handleDeepLink = (event: URLOpenListenerEvent) => {
      console.log('Deep link opened:', event.url);
      
      const data = parseDeepLink(event.url);
      if (data) {
        const route = getRouteFromDeepLink(data);
        console.log('Navigating to:', route);
        history.push(route);
      }
    };
    
    // Listen for deep link events
    App.addListener('appUrlOpen', handleDeepLink);
    
    return () => {
      App.removeAllListeners();
    };
  }, [history]);
};

/**
 * Generate deep link URLs for sharing
 */
export const generateDeepLink = (type: DeepLinkType, id: string, params?: Record<string, string>): string => {
  let url = `mgp://${type}/${id}`;
  
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, value);
    });
    url += `?${searchParams.toString()}`;
  }
  
  return url;
};

// Context for deep link handling
interface DeepLinkContextType {
  generateLink: typeof generateDeepLink;
}

const DeepLinkContext = createContext<DeepLinkContextType>({
  generateLink: generateDeepLink,
});

const useDeepLinkContext = () => useContext(DeepLinkContext);
